import { UserAgent, Web } from 'sip.js';
import RelayVoiceTransport from './relayVoiceTransport';

// Credentials live only in this instance; never persist them in Vuex/storage.
export default class RelayVoiceClient {
  constructor(config, audio, delegate) {
    this.domain = config.domain;
    this.delegate = delegate;
    // 'rfc2833' sends keypad digits as RTP telephone-events (FreeSWITCH's
    // default); 'info' sends SIP INFO, which the switch ignores unless the
    // channel is configured for it. Set by Relay Voice in the session.
    this.dtmfMethod = config.dtmf_method === 'info' ? 'info' : 'rfc2833';
    this.held = false;
    this.user = new Web.SimpleUser(config.server, {
      aor: config.aor,
      delegate,
      media: { constraints: { audio: true, video: false }, remote: { audio } },
      userAgentOptions: {
        transportConstructor: RelayVoiceTransport,
        authorizationUsername: config.username,
        authorizationPassword: config.password,
        displayName: config.display_name,
        logBuiltinEnabled: false,
        logConfiguration: false,
      },
    });
  }

  get session() {
    return this.user.session;
  }

  async connect() {
    await this.user.connect();
    await this.user.register();
  }

  call(number) {
    // No arbitrary SIP URI, domain override, dialplan syntax or premium prefix.
    if (!/^(?:\+[1-9]\d{7,14}|\d{2,6})$/.test(number)) {
      return Promise.reject(new Error('Invalid destination'));
    }
    this.held = false;
    let earlyMedia = false;
    return this.user.call(
      `sip:${number}@${this.domain}`,
      { earlyMedia: true },
      {
        requestDelegate: {
          onProgress: response => {
            const { statusCode, body } = response.message;
            if (statusCode === 183 && body) {
              earlyMedia = true;
              this.delegate.onEarlyMedia?.();
            } else if (statusCode === 180 && !earlyMedia) {
              this.delegate.onCallRinging?.();
            }
          },
        },
      }
    );
  }

  answer() {
    this.held = false;
    return this.user.answer();
  }

  decline() {
    return this.user.decline();
  }

  hangup() {
    return this.user.hangup();
  }

  mute(muted) {
    return muted ? this.user.mute() : this.user.unmute();
  }

  async hold(onHold) {
    await (onHold ? this.user.hold() : this.user.unhold());
    this.held = onHold;
  }

  sendDigit(tone) {
    if (!/^[0-9*#]$/.test(tone))
      return Promise.reject(new Error('Invalid digit'));
    if (this.dtmfMethod === 'info') return this.user.sendDTMF(tone);
    const handler = this.session?.sessionDescriptionHandler;
    if (!handler?.sendDtmf(tone, { duration: 160, interToneGap: 70 })) {
      return Promise.reject(new Error('Digit not sent'));
    }
    return Promise.resolve();
  }

  // Blind transfer to a colleague's extension (SIP REFER). The switch then
  // ends this leg; the customer is connected to the colleague.
  transfer(extension, onRejected = () => {}) {
    if (!/^1\d{3}$/.test(extension) || !this.session) {
      return Promise.reject(new Error('Invalid transfer target'));
    }
    return this.session.refer(
      UserAgent.makeURI(`sip:${extension}@${this.domain}`),
      {
        requestDelegate: { onReject: () => onRejected() },
      }
    );
  }

  disconnect() {
    return this.user.disconnect();
  }
}
